import { useQuery } from "@tanstack/react-query";

import { api } from "../api/rest";
import type { ShadowShape } from "../api/types";

/**
 * Такт опроса формы счёта.
 *
 * Втрое реже сводок, и не из экономии, а по смыслу: форма отвечает на вопрос
 * «из чего сложился счёт за неделю», и ответ на него не меняется от одной новой
 * сделки. Запрос при этом самый дорогой на странице — оконная функция по шести
 * таблицам, — так что частить им означало бы платить за то, чего не увидишь.
 */
const SHAPES_REFETCH_MS = 60_000;

/**
 * Форма счёта по всем теневым журналам разом.
 *
 * ОДИН запрос на страницу, а не по одному на панель. Так границы корзин
 * гарантированно общие (их назначает сервер), а шесть панелей делят один кеш
 * TanStack по ключу окна — открытие панели не добавляет обращения к серверу.
 */
export function useShapes(hours: number, windowLen = 0) {
  return useQuery<{ hours: number; journals: Record<string, ShadowShape[]> }>({
    queryKey: ["shadow-shapes", hours, windowLen],
    queryFn: () => api.shadowShapes(hours, windowLen),
    refetchInterval: SHAPES_REFETCH_MS,
    retry: 1,
  });
}

/**
 * Формы одного журнала. Пустой список означает «сделок не было» и отличается от
 * undefined («ещё не загрузили») намеренно: панель показывает эти два случая
 * по-разному.
 */
export function useJournalShapes(
  hours: number,
  journal: string,
  windowLen = 0,
): ShadowShape[] | undefined {
  const q = useShapes(hours, windowLen);
  return q.data?.journals[journal];
}
